<template>
  <div
    :class="[
      'feed-item',
      { 'feed-item-clickable': clickable },
      { 'feed-item-card': showAsCard }
    ]"
    @click="clickable ? handleSubmissionClick() : null"
  >
    <div class="feed-item-header">
      <div class="user-info">
        <div class="user-avatar">
          {{ submission.user.name[0].toUpperCase() }}
        </div>
        <div class="user-details">
          <div class="user-name-row">
            <span class="user-name">{{ submission.user.name }}</span>
          </div>
          <div class="meta-row">
            <span class="timestamp">{{ relativeTime }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="submission.title" class="feed-item-title">
      {{ submission.title }}
    </div>

    <div class="content-wrapper">
      <div
        ref="contentRef"
        :class="[
          'feed-item-content',
          !isContentExpanded && showReadMore ? 'content-collapsed' : ''
        ]"
      >
        {{ submission.content }}
      </div>
      <button
        v-if="showReadMore"
        @click.stop="toggleContent"
        class="btn-read-more"
      >
        {{ isContentExpanded ? 'Read less' : 'Read more' }}
      </button>
    </div>

    <!-- File Attachments Preview -->
    <div v-if="submission.attachments && submission.attachments.length > 0" class="attachments-preview">
      <div
        v-for="attachment in submission.attachments"
        :key="attachment.id"
        class="attachment-thumbnail"
        @click.stop="handleAttachmentClick"
      >
        <div class="attachment-icon">
          <font-awesome-icon :icon="getFileIcon(attachment.attachment_type)" />
        </div>
        <div class="attachment-info">
          <div class="attachment-filename">{{ attachment.file_name }}</div>
          <div class="attachment-type">{{ getFileTypeLabel(attachment.attachment_type) }}</div>
        </div>
      </div>
    </div>

    <div v-if="showFooter" class="feed-item-footer">
      <button class="comment-button" @click.stop="handleCommentClick">
        <font-awesome-icon :icon="['fas', 'comment']" />
        <span>{{ submission.comment_count || 0 }}</span>
      </button>

      <button v-if="submission.attachment_count > 0" class="attachment-button" @click.stop="handleAttachmentClick">
        <font-awesome-icon :icon="['fas', 'paperclip']" />
        <span>{{ submission.attachment_count }}</span>
      </button>

      <button v-if="submission.data_count > 0" class="data-button" @click.stop="handleDataClick">
        <font-awesome-icon :icon="['fas', 'database']" />
        <span>{{ submission.data_count }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted, nextTick } from 'vue'
import moment from 'moment'

const props = defineProps({
  submission: {
    type: Object,
    required: true
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  clickable: {
    type: Boolean,
    default: true
  },
  showAsCard: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['comment-click', 'submission-click', 'attachment-click', 'data-click'])

const isContentExpanded = ref(false)
const showReadMore = ref(false)
const contentRef = ref(null)

const userBadgeClass = computed(() => {
  if (props.submission.user.type === 1337) return 'badge-admin'
  if (props.submission.user.type >= 2) return 'badge-pro'
  return 'badge-free'
})

const userBadgeText = computed(() => {
  if (props.submission.user.type === 1337) return 'Admin'
  if (props.submission.user.type >= 2) return 'Pro'
  return 'Free'
})

const relativeTime = computed(() => {
  if (!props.submission.created_at) {
    return 'Unknown'
  }
  const date = moment(props.submission.created_at)
  if (!date.isValid()) {
    return 'Unknown'
  }
  return date.fromNow()
})

const toggleContent = () => {
  isContentExpanded.value = !isContentExpanded.value
}

function handleCommentClick() {
  emit('comment-click', props.submission)
}

function handleSubmissionClick() {
  emit('submission-click', props.submission)
}

function handleAttachmentClick() {
  emit('attachment-click', props.submission)
}

function handleDataClick() {
  emit('data-click', props.submission)
}

// File type helpers
function getFileIcon(mimeType) {
  if (!mimeType) return ['fas', 'file']

  if (mimeType.startsWith('image/')) return ['fas', 'file-image']
  if (mimeType.startsWith('video/')) return ['fas', 'file-video']
  if (mimeType.startsWith('audio/')) return ['fas', 'file-audio']
  if (mimeType.includes('pdf')) return ['fas', 'file-pdf']
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return ['fas', 'file-zipper']
  if (mimeType.includes('word') || mimeType.includes('document')) return ['fas', 'file-word']
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return ['fas', 'file-excel']
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return ['fas', 'file-powerpoint']
  if (mimeType.includes('text/')) return ['fas', 'file-lines']
  if (mimeType.includes('json') || mimeType.includes('xml')) return ['fas', 'file-code']

  return ['fas', 'file']
}

function getFileTypeLabel(mimeType) {
  if (!mimeType) return 'File'

  if (mimeType.startsWith('image/')) return 'Image'
  if (mimeType.startsWith('video/')) return 'Video'
  if (mimeType.startsWith('audio/')) return 'Audio'
  if (mimeType.includes('pdf')) return 'PDF'
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'Archive'
  if (mimeType.includes('word') || mimeType.includes('document')) return 'Document'
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet'
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Presentation'
  if (mimeType.includes('text/plain')) return 'Text'
  if (mimeType.includes('text/')) return 'Log'
  if (mimeType.includes('json')) return 'JSON'
  if (mimeType.includes('xml')) return 'XML'

  return 'File'
}

onMounted(async () => {
  // Check content height after render
  await nextTick()
  if (contentRef.value) {
    const height = contentRef.value.scrollHeight
    showReadMore.value = height > 200
  }
})
</script>

<style scoped>
.feed-item {
  transition: background 0.2s;
}

.feed-item-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
  padding: 1rem;
}

.feed-item-clickable {
  cursor: pointer;
}

.feed-item-clickable:hover {
  background: rgba(255, 255, 255, 0.04);
}

.feed-item-header {
  margin-bottom: 0.75rem;
}

.user-info {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7357D2, #8B6FE6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.user-name {
  color: #FFFFFF;
  font-weight: 600;
  font-size: 0.95rem;
}

.user-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
}

.badge-admin {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.badge-pro {
  background: rgba(168, 85, 247, 0.2);
  color: #a855f7;
}

.badge-free {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.channel-badge {
  color: #7357D2;
  font-weight: 500;
}

.timestamp {
  color: #6B7280;
}

.feed-item-title {
  color: #FFFFFF;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.content-wrapper {
  position: relative;
}

.feed-item-content {
  color: #9CA3AF;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-wrap;
  transition: max-height 0.3s ease;
}

.feed-item-content.content-collapsed {
  max-height: 200px;
  overflow: hidden;
}

.btn-read-more {
  margin-top: 0.5rem;
  background: none;
  border: none;
  color: #a855f7;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}

.btn-read-more:hover {
  color: #c084fc;
}

/* Attachments Preview */
.attachments-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}

.attachment-thumbnail {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 0.75rem;
  transition: all 0.2s;
  cursor: pointer;
  min-width: 0;
  max-width: 100%;
}

.attachment-thumbnail:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(115, 87, 210, 0.3);
}

.attachment-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 87, 210, 0.15);
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.attachment-icon svg {
  font-size: 1.25rem;
  color: #8B6FE6;
}

.attachment-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.attachment-filename {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-type {
  color: #6B7280;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.05em;
}

.more-attachments-thumbnail {
  background: rgba(115, 87, 210, 0.08);
  border-color: rgba(115, 87, 210, 0.2);
}

.more-attachments-thumbnail:hover {
  background: rgba(115, 87, 210, 0.12);
  border-color: rgba(115, 87, 210, 0.4);
}

.more-attachments-thumbnail .attachment-icon {
  background: rgba(115, 87, 210, 0.2);
}

.more-attachments-thumbnail .attachment-filename {
  color: #8B6FE6;
  font-weight: 600;
}

.more-attachments-thumbnail .attachment-type {
  color: #7357D2;
}

.feed-item-footer {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  gap: 1rem;
}

.comment-button,
.data-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.comment-button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #7357D2;
}

.attachment-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.attachment-button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #10B981;
}

.data-button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #3B82F6;
}

.comment-button svg,
.attachment-button svg,
.data-button svg {
  width: 1rem;
  height: 1rem;
}

.comment-button span,
.attachment-button span,
.data-button span {
  font-weight: 500;
}
</style>
